import type { AnomalyCode } from './policy/schema';
import type { TimeEventType, RecordStatus, GpsResult, EventSource } from './engine/types';
import type { DayType } from './schedule/types';

export const EVENT_TYPE_LABEL: Record<TimeEventType, string> = {
  CLOCK_IN: 'Entrada',
  BREAK_START: 'Inicio de pausa',
  BREAK_END: 'Fin de pausa',
  CLOCK_OUT: 'Salida',
  AUTO_START: 'Inicio previsto/automático (no personal)',
};

/** Etiqueta de un evento concreto: distingue la salida automática (sistema) de la salida fichada por el trabajador. */
export function eventLabel(e: { type: string; source?: string | null; personal?: boolean | null }): string {
  if (e.type === 'CLOCK_OUT' && e.source === 'SYSTEM' && e.personal === false) return 'Salida automática (no personal)';
  return EVENT_TYPE_LABEL[e.type as TimeEventType] ?? e.type;
}

export const EVENT_SOURCE_LABEL: Record<EventSource, string> = {
  MOBILE: 'App móvil',
  WEB: 'Web',
  KIOSK: 'Kiosco',
  API: 'API',
  CORRECTION: 'Corrección aprobada',
  SYSTEM: 'Sistema',
};

export const RECORD_STATUS_LABEL: Record<RecordStatus, string> = {
  NOT_STARTED: 'Sin iniciar',
  AUTO_STARTED: 'Inicio automático · fichaje personal pendiente',
  WORKING: 'Trabajando',
  ON_BREAK: 'En pausa',
  FINISHED: 'Jornada finalizada',
};

export const DAY_TYPE_LABEL: Record<DayType, string> = {
  WORKDAY: 'Laborable',
  REST: 'Descanso',
  HOLIDAY: 'Festivo',
  CLOSURE: 'Cierre de empresa',
  VACATION: 'Vacaciones',
  ABSENCE: 'Ausencia',
  UNSCHEDULED: 'Sin planificación',
};

export const GPS_RESULT_LABEL: Record<GpsResult, string> = {
  VALID: 'GPS válido',
  OUTSIDE_ZONE: 'Fuera de zona',
  LOW_ACCURACY: 'Precisión insuficiente',
  NO_CENTER: 'Sin centro de referencia',
  UNAVAILABLE: 'Ubicación no disponible',
  PERMISSION_DENIED: 'Permiso denegado',
  NOT_PROVIDED: 'No facilitada',
  NOT_REQUIRED: 'No requerido',
};

export const ANOMALY_LABEL: Record<AnomalyCode, string> = {
  MISSING_CLOCK_IN: 'Entrada no registrada',
  MISSING_CLOCK_OUT: 'Salida no registrada',
  MISSING_BREAK: 'Pausa no registrada',
  LATE_ARRIVAL: 'Llegada tarde',
  EARLY_DEPARTURE: 'Salida anticipada',
  BREAK_EXCEEDED: 'Pausa excedida',
  INCOMPLETE_DAY: 'Jornada incompleta',
  OUTSIDE_ZONE: 'Fichaje fuera de zona',
  DUPLICATE_EVENT: 'Fichaje duplicado',
  INCONSISTENT_EVENTS: 'Eventos inconsistentes',
  UNSCHEDULED_WORK: 'Trabajo fuera de horario',
  WORK_ON_HOLIDAY: 'Trabajo en festivo',
  WORK_DURING_ABSENCE: 'Trabajo durante ausencia',
  GPS_NOT_VALIDATED: 'GPS no validado',
  OFFLINE_REVIEW: 'Fichaje offline pendiente de revisión',
  EXCESS_HOURS: 'Exceso respecto a jornada prevista',
  AUTO_CLOCK_OUT: 'Salida automática (olvido de fichar)',
  AUTO_START_NO_PERSONAL_CLOCK: 'Inicio automático sin fichaje personal',
  LONG_SHIFT: 'Jornada excesivamente larga',
};

/** Tipos de incidencia del sistema. Las empresas pueden crear tipos propios. */
export const SYSTEM_INCIDENT_TYPES: { code: string; name: string; anomaly?: AnomalyCode }[] = [
  { code: 'FORGOT_CLOCK_IN', name: 'Entrada olvidada', anomaly: 'MISSING_CLOCK_IN' },
  { code: 'FORGOT_CLOCK_OUT', name: 'Salida olvidada', anomaly: 'MISSING_CLOCK_OUT' },
  { code: 'FORGOT_BREAK', name: 'Pausa olvidada', anomaly: 'MISSING_BREAK' },
  { code: 'WRONG_CLOCK', name: 'Fichaje incorrecto' },
  { code: 'LATE_ARRIVAL', name: 'Llegada tarde', anomaly: 'LATE_ARRIVAL' },
  { code: 'EARLY_DEPARTURE', name: 'Salida anticipada', anomaly: 'EARLY_DEPARTURE' },
  { code: 'GPS', name: 'Problema de GPS', anomaly: 'GPS_NOT_VALIDATED' },
  { code: 'CONNECTION', name: 'Problema de conexión', anomaly: 'OFFLINE_REVIEW' },
  { code: 'APP', name: 'Problema con la aplicación' },
  { code: 'OUTSIDE_ZONE', name: 'Fichaje fuera de zona', anomaly: 'OUTSIDE_ZONE' },
  { code: 'WRONG_SCHEDULE', name: 'Horario incorrecto' },
  { code: 'OUT_OF_SCHEDULE_WORK', name: 'Trabajo fuera de horario', anomaly: 'UNSCHEDULED_WORK' },
  { code: 'COULD_NOT_CLOCK', name: 'No pude fichar' },
  { code: 'BREAK_EXCEEDED', name: 'Pausa excedida', anomaly: 'BREAK_EXCEEDED' },
  { code: 'INCOMPLETE_DAY', name: 'Jornada incompleta', anomaly: 'INCOMPLETE_DAY' },
  { code: 'DUPLICATE', name: 'Fichaje duplicado', anomaly: 'DUPLICATE_EVENT' },
  { code: 'INCONSISTENT', name: 'Eventos inconsistentes', anomaly: 'INCONSISTENT_EVENTS' },
  { code: 'HOLIDAY_WORK', name: 'Trabajo en festivo', anomaly: 'WORK_ON_HOLIDAY' },
  { code: 'ABSENCE_WORK', name: 'Trabajo durante ausencia', anomaly: 'WORK_DURING_ABSENCE' },
  { code: 'EXCESS', name: 'Exceso de jornada', anomaly: 'EXCESS_HOURS' },
  { code: 'AUTO_START', name: 'Inicio automático sin fichaje personal', anomaly: 'AUTO_START_NO_PERSONAL_CLOCK' },
  { code: 'AUTO_CLOCK_OUT', name: 'Salida automática por olvido', anomaly: 'AUTO_CLOCK_OUT' },
  { code: 'LONG_SHIFT', name: 'Jornada excesivamente larga', anomaly: 'LONG_SHIFT' },
  { code: 'OTHER', name: 'Otra' },
];

export function incidentTypeForAnomaly(code: AnomalyCode): string {
  return SYSTEM_INCIDENT_TYPES.find((t) => t.anomaly === code)?.code ?? 'OTHER';
}

export type IncidentStatus = 'DRAFT' | 'SUBMITTED' | 'IN_REVIEW' | 'INFO_REQUESTED' | 'APPROVED' | 'REJECTED' | 'CLOSED';

export const INCIDENT_STATUS_LABEL: Record<IncidentStatus, string> = {
  DRAFT: 'Borrador',
  SUBMITTED: 'Enviada',
  IN_REVIEW: 'En revisión',
  INFO_REQUESTED: 'Información solicitada',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CLOSED: 'Cerrada',
};

/** Máquina de estados de incidencias: quién puede mover a qué estado. */
export const INCIDENT_TRANSITIONS: Record<IncidentStatus, { to: IncidentStatus; by: 'worker' | 'reviewer' }[]> = {
  DRAFT: [{ to: 'SUBMITTED', by: 'worker' }],
  SUBMITTED: [
    { to: 'IN_REVIEW', by: 'reviewer' },
    { to: 'INFO_REQUESTED', by: 'reviewer' },
    { to: 'APPROVED', by: 'reviewer' },
    { to: 'REJECTED', by: 'reviewer' },
  ],
  IN_REVIEW: [
    { to: 'INFO_REQUESTED', by: 'reviewer' },
    { to: 'APPROVED', by: 'reviewer' },
    { to: 'REJECTED', by: 'reviewer' },
  ],
  INFO_REQUESTED: [
    { to: 'SUBMITTED', by: 'worker' },
    { to: 'REJECTED', by: 'reviewer' },
  ],
  APPROVED: [{ to: 'CLOSED', by: 'reviewer' }],
  REJECTED: [{ to: 'CLOSED', by: 'reviewer' }],
  CLOSED: [],
};

export function canTransitionIncident(from: IncidentStatus, to: IncidentStatus, by: 'worker' | 'reviewer'): boolean {
  return INCIDENT_TRANSITIONS[from].some((t) => t.to === to && t.by === by);
}

export type RequestStatus = 'PENDING' | 'INFO_REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  PENDING: 'Pendiente',
  INFO_REQUESTED: 'Información solicitada',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Cancelada',
};

export const WEBHOOK_EVENTS = [
  'employee.created',
  'employee.updated',
  'employee.deactivated',
  'clock.in',
  'clock.out',
  'break.started',
  'break.ended',
  'incident.created',
  'incident.approved',
  'correction.approved',
  'report.generated',
  'custody.completed',
  'custody.failed',
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

/** Funcionalidades activables por plan/addon. */
export const FEATURES = {
  core: 'Registro horario, planificación, incidencias, informes',
  gps: 'Validación GPS en fichajes',
  kiosk: 'Modo kiosco',
  api: 'API y webhooks',
  cloud: 'WulfTime Cloud: almacenamiento histórico gestionado',
  custody: 'WulfTime Custody: paquetes mensuales con integridad y entrega externa',
  sso: 'SSO / SCIM (preparado)',
  intelligence: 'WulfTime Intelligence (recomendaciones de revisión)',
  advanced_roles: 'Roles y permisos avanzados por ámbito',
} as const;
export type Feature = keyof typeof FEATURES;

/** Paquetes conceptuales. Precios sin definir: se configuran en BD sin tocar código. */
export const DEFAULT_PLANS: { code: string; name: string; description: string; features: Feature[]; limits: { maxEmployees: number | null; storageGb: number | null; apiRequestsPerDay: number | null } }[] = [
  {
    code: 'CORE',
    name: 'WulfTime Core',
    description: 'Registro horario completo.',
    features: ['core', 'gps', 'kiosk'],
    limits: { maxEmployees: 50, storageGb: 5, apiRequestsPerDay: null },
  },
  {
    code: 'CLOUD',
    name: 'WulfTime Cloud',
    description: 'Core + almacenamiento histórico gestionado.',
    features: ['core', 'gps', 'kiosk', 'cloud', 'api'],
    limits: { maxEmployees: 250, storageGb: 50, apiRequestsPerDay: 10000 },
  },
  {
    code: 'ENTERPRISE',
    name: 'WulfTime Enterprise / Custody',
    description: 'Core + Cloud + Custody + funcionalidades empresariales.',
    features: ['core', 'gps', 'kiosk', 'cloud', 'custody', 'api', 'sso', 'intelligence', 'advanced_roles'],
    limits: { maxEmployees: null, storageGb: null, apiRequestsPerDay: null },
  },
];
